import type {
  WeatherData,
  WeatherForecast,
  LocationWeather,
  WeatherAlert,
  RouteWeatherAnalysis,
  StageWeatherInfo,
  TimeWindow
} from './weather.types.js'
import { createLogger } from '../../common/utils/logger.js'
import { TTLCache } from '../../common/utils/cache.js'

const logger = createLogger('WeatherService')

const OPENWEATHER_API_KEY = process.env.OPENWEATHER_API_KEY
const OPENWEATHER_BASE_URL = 'https://api.openweathermap.org/data/3.0'
const CACHE_TTL = 10 * 60 * 1000 // 10 minutes
const LOCATION_NAME_TTL = 24 * 60 * 60 * 1000 // 24 hours

const weatherCache = new TTLCache<LocationWeather>({ ttl: CACHE_TTL, maxEntries: 256 })
const locationNameCache = new TTLCache<string>({ ttl: LOCATION_NAME_TTL, maxEntries: 512 })

type StageInput = {
  id: string
  name: string
  location: { coordinates: { lat: number; lng: number } }
  estimatedArrival?: Date
}

export class WeatherService {
  /**
   * Get current weather + forecast for a location
   */
  async getLocationWeather(
    lat: number,
    lng: number,
    options?: { name?: string }
  ): Promise<LocationWeather> {
    const cacheKey = `${lat.toFixed(2)},${lng.toFixed(2)}`

    // Check cache using TTLCache
    let cached = weatherCache.get(cacheKey)
    if (cached) {
      logger.debug('Weather cache hit', { lat, lng })

      if (options?.name && cached.location.name === 'Unknown') {
        cached = {
          ...cached,
          location: {
            ...cached.location,
            name: options.name
          }
        }
        weatherCache.set(cacheKey, cached)
      }

      return cached
    }

    if (!OPENWEATHER_API_KEY) {
      logger.warn('OPENWEATHER_API_KEY not configured, returning mock data')
      return this.getMockWeather(lat, lng, options?.name)
    }

    try {
      // OneCall API 3.0 - current + forecast + alerts
      const url = `${OPENWEATHER_BASE_URL}/onecall?lat=${lat}&lon=${lng}&appid=${OPENWEATHER_API_KEY}&units=metric&exclude=minutely`

      const response = await fetch(url)
      if (!response.ok) {
        throw new Error(`OpenWeather API error: ${response.status}`)
      }

      const data = await response.json() as {
        current: any
        hourly: any[]
        daily: any[]
        alerts?: any[]
      }

      const locationWeather: LocationWeather = {
        location: {
          name: await this.resolveLocationName(lat, lng, options?.name),
          lat,
          lng
        },
        current: this.parseCurrentWeather(data.current),
        hourly: data.hourly.slice(0, 24).map(hour => this.parseHourlyForecast(hour)),
        daily: data.daily.slice(0, 7).map(day => this.parseDailyForecast(day)),
        alerts: data.alerts?.map(alert => this.parseAlert(alert)) || []
      }

      // Cache result
      weatherCache.set(cacheKey, locationWeather)

      // Track cost (OpenWeather OneCall 3.0 is approx $0.0015 per call after free tier)
      // We track 0.15 cents
      await import('../../services/cost-tracker.service.js').then(({ costTracker }) => {
        costTracker.track({
          provider: 'weather',
          service: 'onecall',
          cost: 0.15,
          usage: 1,
          unit: 'call',
          feature: 'weather-forecast'
        }).catch((err: unknown) => logger.warn('Failed to track weather cost', { error: err }))
      })

      return locationWeather
    } catch (error) {
      logger.error('Failed to fetch weather data', { error, lat, lng })
      return this.getMockWeather(lat, lng, options?.name)
    }
  }

  /**
   * Analyze weather for entire route
   */
  async analyzeRouteWeather(planId: string, stages: StageInput[]): Promise<RouteWeatherAnalysis> {
    const stagesWeather: StageWeatherInfo[] = []
    const warnings: string[] = []
    let highRiskCount = 0

    const weatherResults = await this.loadStageWeather(stages)

    for (const { stage, weather } of weatherResults) {
      const riskLevel = this.calculateRiskLevel(weather)
      const stageWarnings = this.generateWarnings(weather, stage.estimatedArrival)

      stagesWeather.push({
        stageId: stage.id,
        stageName: stage.name,
        location: stage.location.coordinates,
        weather,
        riskLevel,
        warnings: stageWarnings,
        bestTimeToVisit: this.findBestTimeToVisit(weather)
      })

      if (riskLevel === 'high') highRiskCount++
      warnings.push(...stageWarnings)
    }

    const overallRisk = highRiskCount > stages.length / 3 ? 'high'
      : highRiskCount > 0 ? 'medium'
        : 'low'

    return {
      planId,
      stages: stagesWeather,
      overallRisk,
      recommendations: this.generateRecommendations(stagesWeather),
      dangerousTimeWindows: this.findDangerousTimeWindows(stagesWeather)
    }
  }

  /**
   * Get weather comparison for multiple dates
   */
  async getWeatherComparison(
    lat: number,
    lng: number,
    dates: Date[]
  ): Promise<WeatherForecast[]> {
    const weather = await this.getLocationWeather(lat, lng)

    return dates.map(date => {
      const dayIndex = Math.floor((date.getTime() - Date.now()) / (24 * 60 * 60 * 1000))
      return weather.daily[Math.min(dayIndex, 6)] || weather.daily[0]
    })
  }

  // Private helper methods

  private parseCurrentWeather(data: any): WeatherData {
    const precipitationMm =
      typeof data?.rain?.['1h'] === 'number'
        ? data.rain['1h']
        : typeof data?.snow?.['1h'] === 'number'
          ? data.snow['1h']
          : 0

    return {
      temperature: data.temp,
      feelsLike: data.feels_like,
      humidity: data.humidity,
      windSpeed: data.wind_speed * 3.6, // m/s to km/h
      windDirection: data.wind_deg,
      description: data.weather[0].description,
      icon: data.weather[0].icon,
      condition: this.mapCondition(data.weather[0].main),
      precipitation: precipitationMm,
      visibility: data.visibility,
      pressure: data.pressure,
      uvIndex: data.uvi,
      sunrise: new Date(data.sunrise * 1000),
      sunset: new Date(data.sunset * 1000)
    }
  }

  private parseHourlyForecast(data: any): WeatherForecast {
    return {
      date: new Date(data.dt * 1000),
      tempMin: data.temp,
      tempMax: data.temp,
      condition: this.mapCondition(data.weather[0].main),
      icon: data.weather[0].icon,
      precipitation: (data.pop || 0) * 100,
      windSpeed: data.wind_speed * 3.6
    }
  }

  private parseDailyForecast(data: any): WeatherForecast {
    return {
      date: new Date(data.dt * 1000),
      tempMin: data.temp.min,
      tempMax: data.temp.max,
      condition: this.mapCondition(data.weather[0].main),
      icon: data.weather[0].icon,
      precipitation: (data.pop || 0) * 100,
      windSpeed: data.wind_speed * 3.6
    }
  }

  private parseAlert(data: any): WeatherAlert {
    return {
      id: data.event.replace(/\s/g, '-').toLowerCase(),
      event: data.event,
      severity: this.mapSeverity(data.tags),
      description: data.description,
      start: new Date(data.start * 1000),
      end: new Date(data.end * 1000),
      regions: data.tags || []
    }
  }

  private mapCondition(main: string): WeatherData['condition'] {
    const map: Record<string, WeatherData['condition']> = {
      'Clear': 'clear',
      'Clouds': 'clouds',
      'Rain': 'rain',
      'Drizzle': 'rain',
      'Snow': 'snow',
      'Thunderstorm': 'storm',
      'Mist': 'fog',
      'Fog': 'fog',
      'Haze': 'fog'
    }
    return map[main] || 'clouds'
  }

  private mapSeverity(tags: string[]): WeatherAlert['severity'] {
    if (tags?.some(t => t.includes('extreme'))) return 'extreme'
    if (tags?.some(t => t.includes('severe'))) return 'severe'
    if (tags?.some(t => t.includes('moderate'))) return 'moderate'
    return 'minor'
  }

  private calculateRiskLevel(weather: LocationWeather): 'low' | 'medium' | 'high' {
    const { current, alerts } = weather

    if (alerts && alerts.length > 0) {
      const hasHighSeverity = alerts.some(a => a.severity === 'severe' || a.severity === 'extreme')
      if (hasHighSeverity) return 'high'
    }

    if (current.condition === 'storm' || current.windSpeed > 60) return 'high'
    if (current.condition === 'snow' || current.windSpeed > 40) return 'medium'
    if (current.condition === 'rain' && current.windSpeed > 30) return 'medium'

    return 'low'
  }

  private generateWarnings(weather: LocationWeather, estimatedArrival?: Date): string[] {
    const warnings: string[] = []
    const { current, alerts } = weather

    if (alerts) {
      for (const alert of alerts) {
        warnings.push(`⚠️ ${alert.event}: ${alert.description.slice(0, 100)}`)
      }
    }

    if (estimatedArrival) {
      const hoursUntilArrival = (estimatedArrival.getTime() - Date.now()) / (1000 * 60 * 60)
      if (hoursUntilArrival >= 0 && hoursUntilArrival <= 6 && alerts && alerts.length > 0) {
        warnings.push('⏱️ Arrivée prévue pendant une alerte météo active')
      }
    }

    if (current.condition === 'storm') {
      warnings.push('🌩️ Orage prévu - Évitez les zones exposées')
    }

    if (current.windSpeed > 50) {
      warnings.push(`💨 Vents forts (${Math.round(current.windSpeed)} km/h) - Conduite difficile`)
    }

    if (current.condition === 'snow') {
      warnings.push('❄️ Neige - Vérifiez équipements hiver')
    }

    if (current.temperature < 0) {
      warnings.push('🧊 Températures négatives - Risque de verglas')
    }

    if (current.temperature > 35) {
      warnings.push('🌡️ Températures élevées - Hydratez-vous régulièrement')
    }

    return warnings
  }

  private generateRecommendations(stages: StageWeatherInfo[]): string[] {
    const recommendations: string[] = []
    const highRiskStages = stages.filter(s => s.riskLevel === 'high')

    if (highRiskStages.length > 0) {
      recommendations.push(
        `⚠️ ${highRiskStages.length} étape(s) avec conditions météo difficiles - Envisagez de reporter ou modifier l'itinéraire`
      )
    }

    const snowStages = stages.filter(s => s.weather.current.condition === 'snow')
    if (snowStages.length > 0) {
      recommendations.push('❄️ Équipements hiver recommandés (pneus, chaînes)')
    }

    const hotStages = stages.filter(s => s.weather.current.temperature > 32)
    if (hotStages.length > 0) {
      recommendations.push('☀️ Forte chaleur prévue - Climatisation et eau en quantité')
    }

    return recommendations
  }

  private findDangerousTimeWindows(stages: StageWeatherInfo[]): TimeWindow[] {
    const windows: TimeWindow[] = []

    for (const stage of stages) {
      const alerts = stage.weather.alerts ?? []
      for (const alert of alerts) {
        windows.push({
          start: alert.start,
          end: alert.end,
          reason: `${alert.event} (${alert.severity})`,
          affectedStages: [stage.stageId]
        })
      }

      if (stage.riskLevel === 'high') {
        const start = new Date()
        const end = new Date(start.getTime() + 6 * 60 * 60 * 1000)
        windows.push({
          start,
          end,
          reason: 'Conditions à haut risque',
          affectedStages: [stage.stageId]
        })
      }

      const severeHours = stage.weather.hourly
        .filter(hour => hour.windSpeed >= 60 || hour.precipitation >= 70)
        .slice(0, 6)

      if (severeHours.length > 0) {
        const start = severeHours[0].date
        const endHour = severeHours[severeHours.length - 1].date
        const end = new Date(endHour.getTime() + 60 * 60 * 1000)

        windows.push({
          start,
          end,
          reason: 'Fenêtre météo défavorable (vent/pluie)',
          affectedStages: [stage.stageId]
        })
      }
    }

    return this.mergeTimeWindows(windows)
  }

  private mergeTimeWindows(windows: TimeWindow[]): TimeWindow[] {
    const sorted = windows
      .map(window => ({
        ...window,
        affectedStages: Array.from(new Set(window.affectedStages))
      }))
      .slice().sort((a, b) => a.start.getTime() - b.start.getTime())

    const merged: TimeWindow[] = []

    for (const window of sorted) {
      const last = merged[merged.length - 1]
      if (last && last.reason === window.reason && last.end.getTime() >= window.start.getTime()) {
        last.end = new Date(Math.max(last.end.getTime(), window.end.getTime()))
        last.affectedStages = Array.from(new Set([...last.affectedStages, ...window.affectedStages]))
      } else {
        merged.push({ ...window })
      }
    }

    return merged
  }

  private findBestTimeToVisit(weather: LocationWeather): Date | undefined {
    // Find best time in next 24h based on forecast
    const goodWeatherHours = weather.hourly.filter(h =>
      h.condition === 'clear' &&
      h.precipitation < 20 &&
      h.windSpeed < 30
    )

    return goodWeatherHours[0]?.date
  }

  private async resolveLocationName(lat: number, lng: number, fallbackName?: string): Promise<string> {
    if (fallbackName?.trim()) {
      return fallbackName.trim()
    }

    const cacheKey = `${lat.toFixed(2)},${lng.toFixed(2)}`
    const cached = locationNameCache.get(cacheKey)
    if (cached) {
      return cached
    }

    try {
      const url = `https://api.openweathermap.org/geo/1.0/reverse?lat=${lat}&lon=${lng}&limit=1&appid=${OPENWEATHER_API_KEY}`
      const response = await fetch(url)
      const data = await response.json() as Array<{ name?: string }>
      const resolved = data[0]?.name || 'Unknown'
      locationNameCache.set(cacheKey, resolved)
      return resolved
    } catch {
      return 'Unknown'
    }
  }

  private getMockWeather(lat: number, lng: number, name?: string): LocationWeather {
    return {
      location: { name: name ?? 'Unknown', lat, lng },
      current: {
        temperature: 20,
        feelsLike: 19,
        humidity: 60,
        windSpeed: 15,
        windDirection: 180,
        description: 'Partly cloudy',
        icon: '02d',
        condition: 'clouds',
        precipitation: 0,
        visibility: 10000,
        pressure: 1013,
        uvIndex: 5,
        sunrise: new Date(),
        sunset: new Date()
      },
      hourly: [],
      daily: [],
      alerts: []
    }
  }

  private async loadStageWeather(stages: StageInput[]): Promise<Array<{ stage: StageInput; weather: LocationWeather }>> {
    const results: Array<{ stage: StageInput; weather: LocationWeather }> = []
    if (stages.length === 0) {
      return results
    }

    const concurrency = Math.min(3, stages.length)
    let index = 0

    const execute = async () => {
      while (true) {
        const currentIndex = index++
        if (currentIndex >= stages.length) {
          break
        }

        const stage = stages[currentIndex]
        const { lat, lng } = stage.location.coordinates
        const weather = await this.getLocationWeather(lat, lng, { name: stage.name })
        results[currentIndex] = { stage, weather }
      }
    }

    await Promise.all(Array.from({ length: concurrency }, execute))
    return results
  }
}

export const weatherService = new WeatherService()
